import { getConfig } from './config';
import {
  clearDevPasswordHash,
  readDevPasswordHash,
  storeDevPasswordHash,
} from './devPassword';

export interface ForbiddenUserInfo {
  login: string;
  sid: string;
  email?: string;
}

export class ForbiddenError extends Error {
  userInfo?: ForbiddenUserInfo;

  constructor(userInfo?: ForbiddenUserInfo) {
    super('Sie haben keine Berechtigung für diese Aktion.');
    this.name = 'ForbiddenError';
    this.userInfo = userInfo;
  }
}

/**
 * Transport-level failure (server unreachable, proxy error page, malformed response).
 * Carries a message that can be shown to the user as-is.
 */
export class NetworkError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number, options?: ErrorOptions) {
    super(message, options);
    this.name = 'NetworkError';
    this.status = status;
  }
}

export interface GraphQLErrorEntry {
  message: string;
  path?: ReadonlyArray<string | number>;
  locations?: ReadonlyArray<{ line: number; column: number }>;
  extensions?: Record<string, unknown>;
}

export interface GraphQLErrorContext {
  operationName?: string;
  query: string;
  variables?: unknown;
  errors: GraphQLErrorEntry[];
  endpoint: string;
  status: number;
  timestamp: string;
}

export class GraphQLError extends Error {
  readonly context: GraphQLErrorContext;
  /** Backend error code (e.g. VALIDATION_ERROR, DATABASE_CONSTRAINT), if the server sent one. */
  readonly code?: string;

  constructor(message: string, context: GraphQLErrorContext) {
    super(message);
    this.name = 'GraphQLError';
    this.context = context;
    const code = context.errors[0]?.extensions?.['code'];
    this.code = typeof code === 'string' ? code : undefined;
  }
}

const networkMessages = {
  unreachable:
    'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Netzwerkverbindung und versuchen Sie es erneut.',
  unauthenticated:
    'Ihre Sitzung ist abgelaufen. Bitte laden Sie die Seite neu und melden Sie sich erneut an.',
  serverError: (status: number) =>
    `Der Server konnte die Anfrage nicht verarbeiten (Fehler ${status}). Bitte versuchen Sie es erneut.`,
  invalidResponse:
    'Die Antwort des Servers konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.',
  unprocessable: 'Die Anfrage konnte nicht verarbeitet werden.',
  invalidDevPassword:
    'Das Dev-Passwort ist ungültig. Bitte laden Sie die Seite neu und geben Sie es erneut ein.',
} as const;

function unreadableResponseMessage(status: number): string {
  if (status === 401) return networkMessages.unauthenticated;
  if (status >= 500) return networkMessages.serverError(status);
  return networkMessages.invalidResponse;
}

/** One issue per line, bulleted as soon as there is more than one. */
function formatValidationErrors(validationErrors: unknown[]): string {
  const messages = validationErrors
    .map((entry) =>
      typeof entry === 'string'
        ? entry
        : ((entry as { message?: string })?.message ?? ''),
    )
    .filter((message) => message.length > 0);

  if (messages.length <= 1) {
    return messages[0] ?? networkMessages.unprocessable;
  }

  return messages.map((message) => `- ${message}`).join('\n');
}

const OPERATION_NAME_REGEX =
  /\b(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/;

function extractOperationName(query: string): string | undefined {
  return OPERATION_NAME_REGEX.exec(query)?.[1];
}

function getEndpoint(): string {
  const config = getConfig();
  const endpoint = config.GRAPHQL_URL;

  if (!endpoint) {
    throw new Error(
      'GraphQL URL not configured. Set GRAPHQL_URL in /config.json.',
    );
  }

  return endpoint;
}

let pendingHash: Promise<string> | null = null;

async function promptDevPasswordHash(): Promise<string> {
  const password = prompt('Dev-Passwort eingeben:');
  if (!password) throw new Error('Dev password required.');

  const encoded = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

  storeDevPasswordHash(hash);
  return hash;
}

function getDevPasswordHash(): Promise<string> {
  const stored = readDevPasswordHash();
  if (stored) return Promise.resolve(stored);

  pendingHash ??= promptDevPasswordHash().finally(() => {
    pendingHash = null;
  });
  return pendingHash;
}

export function fetcher<TData, TVariables>(
  query: string,
  variables?: TVariables,
  additionalHeaders?: RequestInit['headers'],
) {
  return async (): Promise<TData> => {
    const endpoint = getEndpoint();

    const devHeaders: Record<string, string> = {};
    if (getConfig().DEV_TOOLS) {
      const sid = sessionStorage.getItem('dev-user-sid');
      if (sid) {
        devHeaders['X-Dev-User-SID'] = sid;
      }
    }
    const usesDevPassword =
      getConfig().DEV_PASSWORD && location.hostname !== 'localhost';
    if (usesDevPassword) {
      devHeaders['X-Dev-Password-Hash'] = await getDevPasswordHash();
    }

    let res: Response;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/graphql-response+json',
          ...devHeaders,
          ...additionalHeaders,
        },
        body: JSON.stringify({ query, variables }),
      });
    } catch (cause) {
      throw new NetworkError(networkMessages.unreachable, undefined, { cause });
    }

    if (
      res.status === 401 &&
      usesDevPassword &&
      /invalid dev password/i.test(await res.clone().text())
    ) {
      clearDevPasswordHash();
      throw new NetworkError(networkMessages.invalidDevPassword, 401);
    }

    if (res.status === 403) {
      let userInfo: ForbiddenUserInfo | undefined;
      try {
        userInfo = await res.json();
      } catch {
        // ignore parse errors
      }
      throw new ForbiddenError(userInfo);
    }

    // A proxy error page or a crashed server answers with HTML, which would otherwise surface
    // as a raw "Unexpected token '<'" SyntaxError in the UI.
    let json;
    try {
      json = await res.json();
    } catch (cause) {
      throw new NetworkError(
        unreadableResponseMessage(res.status),
        res.status,
        {
          cause,
        },
      );
    }

    if (json.errors) {
      const errors: GraphQLErrorEntry[] = Array.isArray(json.errors)
        ? json.errors
        : [json.errors];
      const first = errors[0];
      const validationErrors = first?.extensions?.['validationErrors'];

      const message =
        validationErrors && Array.isArray(validationErrors)
          ? formatValidationErrors(validationErrors)
          : (first?.message ?? networkMessages.unprocessable);

      throw new GraphQLError(message, {
        operationName: extractOperationName(query),
        query,
        variables,
        errors,
        endpoint,
        status: res.status,
        timestamp: new Date().toISOString(),
      });
    }
    return json.data;
  };
}
